import React, { useState, useEffect, useCallback, useMemo } from 'react';
import io from 'socket.io-client';
import * as api from '../../utils/api';
import styles from './PositionsPage.module.css';
import PositionCard from '../../components/PositionCard';
import OrderCard from '../../components/OrderCard';
import MessageBox from '../../components/MessageBox';
import ConfirmationModal from '../../components/ConfirmationModal';
import ModifyOrderModal from '../../components/ModifyOrderModal';
import AddQuantityModal from '../../components/AddQuantityModal'; 
import ConvertPositionModal from '../../components/ConvertPositionModal';
import ModifyPositionModal from '../../components/ModifyPositionModal';
import { FaChevronRight, FaBriefcase, FaChartPie, FaListAlt, FaFolderOpen, FaFolder, FaClock, FaCheckCircle, FaTimesCircle, FaInbox } from 'react-icons/fa';
import PortfolioValueCard from '../../components/PortfolioValueCard';
import HoldingsList from '../../components/HoldingsList';
import { usePrices } from '../../context/PriceContext';

const baseURL = process.env.REACT_APP_API_URL;

const PositionsPage = () => {
  const [activeTab, setActiveTab] = useState(localStorage.getItem('positionsPageTab') || 'positions');
  const [portfolio, setPortfolio] = useState({ stockHoldings: [], totalPortfolio: 0 });
  const [positions, setPositions] = useState({ open: [], closed: [] });
  const [orders, setOrders] = useState({ pending: [], executed: [], cancelled: [] });

  // Independent expansion states
  const [isPositionsOpenExpanded, setIsPositionsOpenExpanded] = useState(true);
  const [isPositionsClosedExpanded, setIsPositionsClosedExpanded] = useState(false);
  const [isOrdersPendingExpanded, setIsOrdersPendingExpanded] = useState(true);
  const [isOrdersExecutedExpanded, setIsOrdersExecutedExpanded] = useState(false);
  const [isOrdersCancelledExpanded, setIsOrdersCancelledExpanded] = useState(false);

  const [message, setMessage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [loading, setLoading] = useState(true);

  // State for Modify Order Modal
  const [isModifyModalOpen, setIsModifyModalOpen] = useState(false);
  const [orderToModify, setOrderToModify] = useState(null);

  // State for Add Quantity Modal
  const [isAddQuantityModalOpen, setIsAddQuantityModalOpen] = useState(false);
  const [positionToAddQuantity, setPositionToAddQuantity] = useState(null);

  // State for Convert Position Modal
  const [isConvertModalOpen, setIsConvertModalOpen] = useState(false);
  const [positionToConvert, setPositionToConvert] = useState(null);

  // State for Modify Position Modal (Target/SL)
  const [isModifyPositionModalOpen, setIsModifyPositionModalOpen] = useState(false);
  const [positionToModify, setPositionToModify] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [portfolioRes, ordersRes] = await Promise.all([
        api.getPortfolio(),
        api.getOrders(),
      ]);

      if (portfolioRes.data) {
        setPortfolio(portfolioRes.data);
      }

      if (ordersRes.data) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const filteredData = ordersRes.data.filter(order => {
          const orderDate = new Date(order.orderTime || order.createdAt);
          orderDate.setHours(0, 0, 0, 0);

          const isToday = orderDate.getTime() === today.getTime();
          // Show OPEN Carryforward positions even if from previous days
          const isCarryforwardOpen = order.positionType === 'Carryforward' && order.orderStatus === 'Open';

          return isToday || isCarryforwardOpen;
        });

        const open = filteredData.filter(p => p.orderStatus === 'Open');
        const closed = filteredData.filter(p => p.orderStatus === 'Closed');
        const pending = filteredData.filter(p => p.orderStatus === 'Pending');
        const cancelled = filteredData.filter(p => p.orderStatus === 'Cancelled');
        const executed = [...open, ...closed];

        setPositions({ open, closed });
        setOrders({ pending, executed, cancelled });
      }
      setLoading(false);
    } catch (err) {
      console.error("Error fetching data:", err);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();

    const token = localStorage.getItem('token');
    const backendSocket = io(baseURL, {
      auth: { token: token }
    });

    backendSocket.on('orderUpdate', () => fetchData());

    return () => backendSocket.disconnect();
  }, [fetchData]);

  // --- Summary live price logic using usePrices hook ---
  const activeSymbols = useMemo(() => {
    return [
      ...new Set([
        ...(positions.open || []).map(o => o.tradeSymbol),
        ...(orders.pending || []).map(o => o.tradeSymbol)
      ])
    ];
  }, [positions.open, orders.pending]);

  const livePrices = usePrices(activeSymbols);

  // Calculate Summary Stats
  const summaryStats = useMemo(() => {
    // 1. Positions P&L (Today) - Derivative only
    let unrealizedPnl = 0;
    let realizedPnl = 0;

    positions.open.forEach(pos => {
      const lp = livePrices[pos.tradeSymbol] || pos.openPrice;
      const pnl = (lp - pos.openPrice) * pos.quantity * (pos.tradeType === 'buy' ? 1 : -1);
      unrealizedPnl += pnl;
    });

    positions.closed.forEach(pos => {
      realizedPnl += (pos.finalPnL || 0);
    });

    // 2. Portfolio Stats - Holdings only
    const stockVal = portfolio.totalPortfolio?.value || 0;
    const stockInv = portfolio.totalPortfolio?.investment || 0;
    const stockPnlOverall = stockVal - stockInv;
    const stockPnlToday = portfolio.totalPortfolio?.change || 0;

    return {
      holdingsTotalValue: stockVal,
      portfolioOverallPnl: stockPnlOverall,
      portfolioTodayPnl: stockPnlToday,
      realizedPnl,
      unrealizedPnl,
      totalPositionsTodayPnl: realizedPnl + unrealizedPnl,
      openPositionsCount: positions.open.length,
      pendingOrdersCount: orders.pending.length
    };
  }, [positions, livePrices, portfolio.totalPortfolio, orders.pending]);

  useEffect(() => {
    localStorage.setItem('positionsPageTab', activeTab);
  }, [activeTab]);

  const handleOpenModifyModal = (order) => {
    setOrderToModify(order);
    setIsModifyModalOpen(true);
  };

  const handleCloseModifyModal = () => {
    setOrderToModify(null);
    setIsModifyModalOpen(false);
  };

  const handleModifyOrder = async (orderId, formData) => {
    try {
      const payload = { ...formData };
      if (formData.orderType === 'market') {
        payload.livePrice = livePrices[orderToModify.tradeSymbol];
      }
      await api.modifyOrder(orderId, payload);
      setMessage({ type: 'success', text: 'Order modified successfully!' });
      handleCloseModifyModal();
      fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to modify order.' });
    }
  };

  const handleAddQuantity = async (orderId, additionalQty) => {
    try {
      await api.addOrderQuantity(orderId, { additionalQty, price: livePrices[positionToAddQuantity.tradeSymbol] });
      setMessage({ type: 'success', text: 'Quantity added successfully!' });
      setIsAddQuantityModalOpen(false);
      setPositionToAddQuantity(null);
      fetchData();
    } catch (err) {
      if (err.response && err.response.data && err.response.data.msg) {
        setMessage({ type: 'error', text: `Action failed: ${err.response.data.msg}` });
      } else {
        setMessage({ type: 'error', text: 'An unexpected error occurred. Please try again.' });
      }
    }
  };

  const handleConvertPosition = async (orderId) => {
    try {
      await api.convertPosition(orderId);
      setMessage({ type: 'success', text: 'Position converted successfully!' });
      setIsConvertModalOpen(false);
      setPositionToConvert(null);
      fetchData();
    } catch (err) {
      if (err.response && err.response.data && err.response.data.msg) {
        setMessage({ type: 'error', text: `Action failed: ${err.response.data.msg}` });
      } else {
        setMessage({ type: 'error', text: 'An unexpected error occurred. Please try again.' });
      }
    }
  };

  const handleModifyPosition = async (orderId, data) => {
    try {
      await api.modifyOrder(orderId, data);
      setMessage({ type: 'success', text: 'Target/Stoploss updated!' });
      setIsModifyPositionModalOpen(false);
      setPositionToModify(null);
      fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to update Target/Stoploss.' });
    }
  };

  const handleAction = async (type, data, livePrice) => {
    try {
      if (type === 'cancel') {
        await api.cancelOrder(data);
        setMessage({ type: 'success', text: 'Order cancelled successfully!' });
      } else if (type === 'close') {
        const positionToClose = positions.open.find(p => p._id === data);
        setSelectedPosition(positionToClose);
        setIsModalOpen(true);
      } else if (type === 'addQty') {
        const position = positions.open.find(p => p._id === data);
        setPositionToAddQuantity(position);
        setIsAddQuantityModalOpen(true);
      } else if (type === 'convert') {
        const position = positions.open.find(p => p._id === data);
        setPositionToConvert(position);
        setIsConvertModalOpen(true);
      } else if (type === 'modifyPosition') {
        const position = positions.open.find(p => p._id === data);
        setPositionToModify(position);
        setIsModifyPositionModalOpen(true);
      }
      fetchData();
    } catch (err) {
      if (err.response && err.response.data && err.response.data.msg) {
        setMessage({ type: 'error', text: `Action failed: ${err.response.data.msg}` });
      } else {
        setMessage({ type: 'error', text: 'An unexpected error occurred. Please try again.' });
      }
    }
  };

  const handleConfirmClose = async (orderId) => {
    try {
      await api.closePosition(orderId, livePrices[selectedPosition.tradeSymbol]);
      setMessage({ type: 'success', text: 'Position closed successfully!' });
      setIsModalOpen(false);
      fetchData();
    } catch (err) {
      if (err.response && err.response.data && err.response.data.msg) {
        setMessage({ type: 'error', text: `Action failed: ${err.response.data.msg}` });
      } else {
        setMessage({ type: 'error', text: 'An unexpected error occurred. Please try again.' });
      }
    }
  };

  const handleCloseMessage = () => {
    setMessage(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  const renderTabs = () => (
    <div className={styles.tabsContainer}>
      <div className={styles.mainTabs}>
        <button className={activeTab === 'portfolio' ? styles.activeTab : ''} onClick={() => setActiveTab('portfolio')}>
          <FaBriefcase />
          <span>Portfolio</span>
        </button>
        <button className={activeTab === 'positions' ? styles.activeTab : ''} onClick={() => setActiveTab('positions')}>
          <FaChartPie />
          <span>Positions</span>
        </button>
        <button className={activeTab === 'orders' ? styles.activeTab : ''} onClick={() => setActiveTab('orders')}>
          <FaListAlt />
          <span>Orders</span>
        </button>
      </div>
    </div>
  );

  const renderPortfolioContent = () => (
    <div className={styles.contentArea}>
      {!loading && portfolio.stockHoldings && portfolio.stockHoldings.length === 0 ? (
        <div className={styles.emptyText}>
          <FaInbox className={styles.emptyIcon} />
          <p>No Portfolio holdings found</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
          <PortfolioValueCard portfolio={portfolio} />
          {portfolio.stockHoldings && portfolio.stockHoldings.length > 0 && (
            <div style={{ width: '100%' }}>
              <HoldingsList holdings={portfolio.stockHoldings} title="Stock Holdings" />
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderPositions = () => (
    <div className={styles.contentArea}>
      <div className={styles.positionSectionWrapper}>
        <div className={`${styles.positionLabel} ${isPositionsOpenExpanded ? styles.expanded : ''}`} onClick={() => setIsPositionsOpenExpanded(!isPositionsOpenExpanded)}>
          <div className={styles.labelLeft}>
            <FaFolderOpen className={styles.inlineLabelIcon} />
            <span>Open Positions ({positions.open.length})</span>
          </div>
          <FaChevronRight className={styles.chevron} />
        </div>
        {isPositionsOpenExpanded && (
          <div className={styles.positionsList}>
            {positions.open.map(pos => (
              <PositionCard key={pos._id} order={pos} livePrice={livePrices[pos.tradeSymbol]} onAction={handleAction} />
            ))}
            {!loading && positions.open.length === 0 && (
              <div className={styles.emptyText}>
                <FaInbox className={styles.emptyIcon} />
                <p>No open positions</p>
              </div>
            )}
          </div>
        )}
      </div>
      <div className={styles.positionSectionWrapper}>
        <div className={`${styles.positionLabel} ${isPositionsClosedExpanded ? styles.expanded : ''}`} onClick={() => setIsPositionsClosedExpanded(!isPositionsClosedExpanded)}>
          <div className={styles.labelLeft}>
            <FaFolder className={styles.inlineLabelIcon} />
            <span>Closed Positions ({positions.closed.length})</span>
          </div>
          <FaChevronRight className={styles.chevron} />
        </div>
        {isPositionsClosedExpanded && (
          <div className={styles.positionsList}>
            {positions.closed.map(pos => (
              <PositionCard key={pos._id} order={pos} livePrice={livePrices[pos.tradeSymbol]} onAction={handleAction} />
            ))}
            {!loading && positions.closed.length === 0 && (
              <div className={styles.emptyText}>
                <FaInbox className={styles.emptyIcon} />
                <p>No closed positions</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const renderOrders = () => (
    <div className={styles.contentArea}>
      <div className={styles.positionSectionWrapper}>
        <div className={`${styles.positionLabel} ${isOrdersPendingExpanded ? styles.expanded : ''}`} onClick={() => setIsOrdersPendingExpanded(!isOrdersPendingExpanded)}>
          <div className={styles.labelLeft}>
            <FaClock className={styles.inlineLabelIcon} />
            <span>Pending Orders ({orders.pending.length})</span>
          </div>
          <FaChevronRight className={styles.chevron} />
        </div>
        {isOrdersPendingExpanded && (
          <div className={styles.positionsList}>
            {orders.pending.map(order => (
              <OrderCard key={order._id} order={order} livePrice={livePrices[order.tradeSymbol]} onAction={handleAction} onOpenModify={handleOpenModifyModal} />
            ))}
            {!loading && orders.pending.length === 0 && (
              <div className={styles.emptyText}>
                <FaInbox className={styles.emptyIcon} />
                <p>No pending orders</p>
              </div>
            )}
          </div>
        )}
      </div>
      <div className={styles.positionSectionWrapper}>
        <div className={`${styles.positionLabel} ${isOrdersExecutedExpanded ? styles.expanded : ''}`} onClick={() => setIsOrdersExecutedExpanded(!isOrdersExecutedExpanded)}>
          <div className={styles.labelLeft}>
            <FaCheckCircle className={styles.inlineLabelIcon} />
            <span>Executed Orders ({orders.executed.length})</span>
          </div>
          <FaChevronRight className={styles.chevron} />
        </div>
        {isOrdersExecutedExpanded && (
          <div className={styles.positionsList}>
            {orders.executed.map(order => (
              <OrderCard key={order._id} order={order} />
            ))}
            {!loading && orders.executed.length === 0 && (
              <div className={styles.emptyText}>
                <FaInbox className={styles.emptyIcon} />
                <p>No executed orders</p>
              </div>
            )}
          </div>
        )}
      </div>
      <div className={styles.positionSectionWrapper}>
        <div className={`${styles.positionLabel} ${isOrdersCancelledExpanded ? styles.expanded : ''}`} onClick={() => setIsOrdersCancelledExpanded(!isOrdersCancelledExpanded)}>
          <div className={styles.labelLeft}>
            <FaTimesCircle className={styles.inlineLabelIcon} />
            <span>Cancelled Orders ({orders.cancelled.length})</span>
          </div>
          <FaChevronRight className={styles.chevron} />
        </div>
        {isOrdersCancelledExpanded && (
          <div className={styles.positionsList}>
            {orders.cancelled.map(order => (
              <OrderCard key={order._id} order={order} />
            ))}
            {!loading && orders.cancelled.length === 0 && (
              <div className={styles.emptyText}>
                <FaInbox className={styles.emptyIcon} />
                <p>No cancelled orders</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'portfolio':
        return renderPortfolioContent();
      case 'positions':
        return renderPositions();
      case 'orders':
        return renderOrders();
      default:
        return null;
    }
  };

  const renderHeader = () => {
    if (activeTab === 'portfolio') {
      return <PortfolioValueCard portfolio={portfolio} />;
    }

    return (
      <div className={styles.pageHeader}>
        <p className={styles.headerTitle}>Today's Positions P&L</p>
        <h1 className={styles.headerMainValue}>
          {summaryStats.totalPositionsTodayPnl >= 0 ? '+' : ''}{formatCurrency(summaryStats.totalPositionsTodayPnl)}
        </h1>
        <div className={styles.headerStatsRow}>
          <div className={styles.headerStatItem}>
            <span className={styles.headerStatLabel}>Realized</span>
            <span className={`${styles.headerStatValue} ${summaryStats.realizedPnl >= 0 ? styles.positive : styles.negative}`}>
              {summaryStats.realizedPnl >= 0 ? '+' : ''}{formatCurrency(summaryStats.realizedPnl)}
            </span>
          </div>
          <div className={styles.headerStatItem}>
            <span className={styles.headerStatLabel}>Unrealized</span>
            <span className={`${styles.headerStatValue} ${summaryStats.unrealizedPnl >= 0 ? styles.positive : styles.negative}`}>
              {summaryStats.unrealizedPnl >= 0 ? '+' : ''}{formatCurrency(summaryStats.unrealizedPnl)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.positionsPage}>
      {message && <MessageBox message={message.text} type={message.type} onClose={handleCloseMessage} />}

      {renderHeader()}

      {renderTabs()}
      {renderContent()}

      {isModalOpen && selectedPosition && (
        <ConfirmationModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          position={selectedPosition}
          livePrice={livePrices[selectedPosition.tradeSymbol]}
          onConfirm={handleConfirmClose}
        />
      )}
      {isModifyModalOpen && (
        <ModifyOrderModal 
          isOpen={isModifyModalOpen}
          onClose={handleCloseModifyModal}
          order={orderToModify}
          onModify={handleModifyOrder}
          livePrice={livePrices[orderToModify.tradeSymbol]}
        />
      )}
      {isAddQuantityModalOpen && positionToAddQuantity && (
        <AddQuantityModal
          isOpen={isAddQuantityModalOpen}
          onClose={() => setIsAddQuantityModalOpen(false)}
          order={positionToAddQuantity}
          onAddQuantity={handleAddQuantity}
          livePrice={livePrices[positionToAddQuantity.tradeSymbol]}
        />
      )}
      {isConvertModalOpen && positionToConvert && (
        <ConvertPositionModal
          isOpen={isConvertModalOpen}
          onClose={() => setIsConvertModalOpen(false)}
          position={positionToConvert}
          onConvert={handleConvertPosition}
          livePrice={livePrices[positionToConvert.tradeSymbol]}
        />
      )}
      {isModifyPositionModalOpen && positionToModify && (
        <ModifyPositionModal
          isOpen={isModifyPositionModalOpen}
          onClose={() => setIsModifyPositionModalOpen(false)}
          position={positionToModify}
          onModify={handleModifyPosition}
          livePrice={livePrices[positionToModify.tradeSymbol]}
        />
      )}
    </div>
  );
};

export default PositionsPage;